import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
    GetPostReport,
    AnalyzePostMetrics,
    CreateUpdatePostVariation,
    GetPostVariationsList,
    DeletePostVariation,
    GetPostVariationThumbnailUploadURL,
    GetPostVariationResult,
    PromotePostVariation,
    UploadFile,
} from '../../Client/ManagementServiceClient';
import { formatNumber, formatCurrency, formatPercentage, UNKNOWN } from '../../utils/formatters';
import './styles.css';

// How each publish state reads to the user, and whether the report body is
// worth showing. PostState is NOT the signal here — it is the visibility a
// channel publishes with and never advances on its own; the backend derives
// these from the per-channel PostMapping rows instead.
const PUBLISH_STATE_META = {
    Draft: {
        tone: 'idle',
        label: 'Draft',
        title: 'No channels picked yet',
        detail: 'Go back to the Post step and choose where this should go out.',
    },
    Scheduled: {
        tone: 'pending',
        label: 'Scheduled',
        title: 'Scheduled, not out yet',
        detail: 'Results appear here once it publishes and the channels start reporting.',
    },
    Publishing: {
        tone: 'pending',
        label: 'Publishing',
        title: 'Publishing now',
        detail: 'The post is queued with your channels. This usually confirms within a few minutes.',
    },
    Partial: { tone: 'live', label: 'Partly live' },
    Published: { tone: 'live', label: 'Live' },
    Failed: {
        tone: 'error',
        label: 'Failed',
        title: 'The channels rejected this post',
        detail: 'Check the error on each channel below, fix it in the Post step, and publish again.',
    },
};

const IMPACT_ORDER = { high: 0, medium: 1, low: 2 };

// States that resolve on their own once the sync worker runs, so the report
// polls instead of leaving the user on a stale "queued" until they refresh.
const IN_FLIGHT_STATES = new Set(['Publishing', 'Scheduled']);
const POLL_INTERVAL_MS = 15000;
// ~10 minutes. Publishing goes out via a worker scheduled a few minutes after
// the post is due, so give it room, then stop rather than poll forever.
const MAX_POLLS = 40;

const isoDate = (date) => date.toISOString().slice(0, 10);

// The API returns {Num, Message}; its wording is internal.
const AB_ERROR_MESSAGES = {
    401: 'This post already has the maximum of 10 A/B tests. Remove one to add another.',
    402: 'Those dates overlap another test on this post. Only one test can run at a time.',
};

const abErrorMessage = (detail, fallback) => {
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail?.Num && AB_ERROR_MESSAGES[detail.Num]) return AB_ERROR_MESSAGES[detail.Num];
    return detail?.Message || fallback;
};

// Same rule the server enforces: touching days count, because a test ending on
// the 10th and one starting on the 10th are both live that day.
const overlapsExisting = (startDate, endDate, existing, excludeId = '') => existing.some((variation) => {
    if (excludeId && variation.VariationId === excludeId) return false;
    const otherStart = String(variation.StartDate || '').slice(0, 10);
    const otherEnd = String(variation.EndDate || '').slice(0, 10);
    if (!otherStart || !otherEnd) return false;
    return otherStart <= endDate && otherEnd >= startDate;
});

const VERDICT_LABELS = {
    Better: 'Better',
    Worse: 'Worse',
    Inconclusive: 'No clear winner',
    NoData: 'No data yet',
};

// A rate is a percentage; everything else is a count.
const formatMetric = (label, value) => (
    label === 'Engagement rate' ? formatPercentage(value) : formatNumber(value)
);

const formatChange = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return UNKNOWN;
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const changeClass = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    if (value > 0) return 'pr-change-up';
    if (value < 0) return 'pr-change-down';
    return '';
};

// Turn an accepted suggestion into A/B form values. Fields the suggestion does
// not touch are left blank, which the backend reads as "keep the original".
const seedFromRecommendation = (rec, analysis) => {
    const seed = { title: '', description: '', tags: '', note: rec.Title || '' };
    if (rec.Field === 'title') seed.title = rec.ProposedValue;
    if (rec.Field === 'description') seed.description = rec.ProposedValue;
    if (rec.Field === 'hashtags') seed.tags = rec.ProposedValue;
    if (!seed.title) {
        // A variation needs a title; fall back to the AI's suggested one, then
        // the live post's, so accepting a description-only idea still works.
        seed.title = analysis?.AbTest?.Title || '';
    }
    return seed;
};

const seedFromAbTest = (abTest) => ({
    title: abTest?.Title || '',
    description: abTest?.Description || '',
    tags: (abTest?.Tags || []).join(' '),
    note: abTest?.Hypothesis || '',
});

const formatSeconds = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return UNKNOWN;
    const total = Math.round(value);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const formatStamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

// The Report step of a project: did it go out, how is it doing, and what to do
// about it.
function ProjectReport({ post, onAnalysis }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisError, setAnalysisError] = useState('');
    const [isPolling, setIsPolling] = useState(false);
    const [pollTick, setPollTick] = useState(0);
    const [variations, setVariations] = useState([]);
    const [variationResults, setVariationResults] = useState({});
    const [showAbForm, setShowAbForm] = useState(false);
    // Seeds the form when a suggestion is turned into a test.
    const [abSeed, setAbSeed] = useState(null);
    const [abSaving, setAbSaving] = useState(false);
    const [abError, setAbError] = useState('');

    // What this video taught is what the next one starts from: the page
    // that owns "Start the next video" needs it, so it is handed up.
    useEffect(() => {
        onAnalysis?.(report?.Analysis || null);
    }, [report, onAnalysis]);
    const pollCountRef = useRef(0);

    const postId = post?.PostId;

    const loadReport = useCallback(async () => {
        if (!postId) {
            setLoading(false);
            return;
        }
        try {
            setError('');
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await GetPostReport(idToken, postId);
            if (status === 200) {
                setReport(myJson);
                return myJson;
            }
            setError('Could not load this post’s results.');
        } catch {
            setError('Could not load this post’s results.');
        } finally {
            setLoading(false);
        }
        return null;
    }, [postId]);

    const loadVariations = useCallback(async () => {
        if (!postId) return;
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await GetPostVariationsList(idToken, {
                Direction: -1,
                LastEntityId: '',
                PageSize: 20,
            });
            if (status !== 200) return;

            const mine = (myJson.Variations || []).filter(v => v.PostId === postId);
            setVariations(mine);

            // Each variation's outcome is computed on demand, so fetch them
            // alongside rather than blocking the list on all of them.
            const outcomes = await Promise.all(mine.map(async (variation) => {
                try {
                    const res = await GetPostVariationResult(idToken, variation.VariationId);
                    return res.status === 200 ? [variation.VariationId, res.myJson] : null;
                } catch {
                    return null;
                }
            }));
            setVariationResults(Object.fromEntries(outcomes.filter(Boolean)));
        } catch {
            // A/B tests are supplementary; the report still stands without them.
        }
    }, [postId]);

    const promoteVariation = async (variationId) => {
        setAbError('');
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await PromotePostVariation(idToken, variationId);
            if (status !== 200) {
                setAbError(
                    typeof myJson?.detail === 'string' ? myJson.detail : 'Could not keep that version.'
                );
                return;
            }
            await Promise.all([loadReport(), loadVariations()]);
        } catch {
            setAbError('Could not keep that version.');
        }
    };

    useEffect(() => {
        loadReport();
        loadVariations();
    }, [loadReport, loadVariations]);

    // Publishing happens in a background worker, so the first render almost
    // always says "Publishing". Poll until it settles instead of stranding the
    // user on a stale state behind a manual Refresh.
    const publishState = report?.PublishState;
    useEffect(() => {
        if (!publishState || !IN_FLIGHT_STATES.has(publishState)) {
            setIsPolling(false);
            pollCountRef.current = 0;
            return undefined;
        }
        if (pollCountRef.current >= MAX_POLLS) {
            setIsPolling(false);
            return undefined;
        }

        setIsPolling(true);
        // Driven by a tick rather than the report's identity: a failed poll
        // leaves `report` unchanged, which would silently end the loop.
        const timer = setTimeout(async () => {
            pollCountRef.current += 1;
            await loadReport();
            setPollTick(tick => tick + 1);
        }, POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [publishState, pollTick, loadReport]);

    // A/B tests are created here rather than by sending people to a separate
    // page: the numbers that justify the test are on this screen.
    const createVariation = async (form) => {
        setAbSaving(true);
        setAbError('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const { status, myJson } = await CreateUpdatePostVariation(idToken, {
                VariationId: '',
                PostId: postId,
                AlternativeTitle: form.title.trim(),
                AlternativeDescription: form.description.trim(),
                AlternativeTags: form.tags,
                AlternativeThumbnail: Boolean(form.thumbnailFile),
                UserId: user.uid,
                StartDate: form.startDate,
                EndDate: form.endDate,
                // ISO 8601, not .NET ticks — the API models these as pydantic
                // datetimes and reads a bare int as a Unix timestamp.
                CreatedDateTime: new Date().toISOString(),
                ModifiedDateTime: new Date().toISOString(),
                Errors: '',
            });

            if (status !== 200) {
                setAbError(abErrorMessage(myJson?.detail, 'Could not create the A/B test. Please try again.'));
                return false;
            }

            // The image can only be uploaded once the variation exists, since
            // the upload URL is derived from its id.
            if (form.thumbnailFile && myJson?.VariationId) {
                try {
                    const upload = await GetPostVariationThumbnailUploadURL(idToken, myJson.VariationId);
                    if (upload.status === 200 && upload.myJson?.upload_url) {
                        await UploadFile(upload.myJson.upload_url, form.thumbnailFile);
                    } else {
                        setAbError('The test was created, but the alternative image did not upload.');
                    }
                } catch {
                    setAbError('The test was created, but the alternative image did not upload.');
                }
            }

            setShowAbForm(false);
            setAbSeed(null);
            await loadVariations();
            return true;
        } catch {
            setAbError('Could not create the A/B test. Please try again.');
            return false;
        } finally {
            setAbSaving(false);
        }
    };

    // Accepting a suggestion opens the form already filled in, so the only
    // decision left is when to run it.
    const startAbTest = (seed) => {
        setAbSeed(seed || null);
        setAbError('');
        setShowAbForm(true);
    };

    const removeVariation = async (variationId) => {
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status } = await DeletePostVariation(idToken, variationId);
            if (status === 200) {
                setVariations(prev => prev.filter(v => v.VariationId !== variationId));
            }
        } catch {
            setAbError('Could not delete that A/B test.');
        }
    };

    const runAnalysis = async () => {
        setAnalyzing(true);
        setAnalysisError('');
        try {
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await AnalyzePostMetrics(idToken, postId);
            if (status === 200) {
                setReport(myJson);
            } else {
                setAnalysisError(
                    typeof myJson?.detail === 'string'
                        ? myJson.detail
                        : 'Could not analyze the metrics right now. Please try again.'
                );
            }
        } catch {
            setAnalysisError('Could not analyze the metrics right now. Please try again.');
        } finally {
            setAnalyzing(false);
        }
    };

    if (!postId) {
        return <div className="pr-empty">No post is linked to this project yet.</div>;
    }

    if (loading) {
        return <div className="pr-empty">Loading results…</div>;
    }

    // Only take over the screen when there is nothing to show; a failed poll
    // should not replace a report the user is already reading.
    if (error && !report) {
        return (
            <div className="pr-empty">
                {error}
                <button type="button" className="pr-btn" onClick={loadReport}>Try again</button>
            </div>
        );
    }

    const currentState = publishState || 'Draft';
    const meta = PUBLISH_STATE_META[currentState] || PUBLISH_STATE_META.Draft;
    const channels = report?.Channels || [];
    const totals = report?.Totals || {};
    const analysis = report?.Analysis;

    // Nothing is live: say what is happening instead of showing empty tiles.
    if (!report?.IsPublished) {
        return (
            <div className="project-report">
                <div className={`pr-waiting tone-${meta.tone}`}>
                    <span className={`pr-badge tone-${meta.tone}`}>{meta.label}</span>
                    <h3>{meta.title}</h3>
                    <p>{meta.detail}</p>
                    {report?.ScheduledFor && currentState === 'Scheduled' && (
                        <p className="pr-muted">Goes out {formatStamp(report.ScheduledFor)}.</p>
                    )}
                    {channels.some(c => c.ErrorMessage) && (
                        <ul className="pr-channel-errors">
                            {channels.filter(c => c.ErrorMessage).map(c => (
                                <li key={c.ChannelId || c.ChannelType}>
                                    <strong>{c.ChannelName || c.ChannelType}:</strong> {c.ErrorMessage}
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="pr-waiting-actions">
                        {isPolling ? (
                            <span className="pr-checking">Checking for updates…</span>
                        ) : (
                            <button type="button" className="pr-btn" onClick={loadReport}>Refresh</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const tiles = [
        { label: 'Views', value: formatNumber(totals.ViewCount) },
        { label: 'Impressions', value: formatNumber(totals.Impressions) },
        { label: 'CTR', value: formatPercentage(totals.CTR) },
        { label: 'Engagement', value: formatPercentage(totals.EngagementRate) },
        { label: 'Avg. watched', value: formatSeconds(totals.AverageViewDuration) },
        { label: 'Earnings', value: formatCurrency(totals.Earnings) },
    ];

    const sortedRecommendations = [...(analysis?.Recommendations || [])].sort(
        (a, b) => (IMPACT_ORDER[a.Impact] ?? 1) - (IMPACT_ORDER[b.Impact] ?? 1)
    );

    return (
        <div className="project-report">
            <div className="pr-head">
                <div className="pr-head-text">
                    <span className={`pr-badge tone-${meta.tone}`}>{meta.label}</span>
                    <h3>{report.Title || post.Title || 'Posted video'}</h3>
                </div>
                <button type="button" className="pr-btn" onClick={loadReport}>Refresh</button>
            </div>

            <div className="pr-tiles">
                {tiles.map(tile => (
                    <div className="pr-tile" key={tile.label}>
                        <span className="pr-tile-label">{tile.label}</span>
                        <span className="pr-tile-value">{tile.value}</span>
                    </div>
                ))}
            </div>

            <p className="pr-muted pr-tiles-note">
                {report.MetricsUpdatedOn
                    ? `Channel data as of ${formatStamp(report.MetricsUpdatedOn)}. Metrics sync daily.`
                    : 'No channel has reported numbers yet. Metrics sync daily after publishing.'}
                {' '}A “{UNKNOWN}” means the channel does not report that metric.
            </p>

            {channels.length > 1 && (
                <div className="pr-channels">
                    {channels.map(channel => (
                        <div className="pr-channel" key={channel.ChannelId || channel.ChannelType}>
                            <div className="pr-channel-head">
                                <span className="pr-channel-name">{channel.ChannelName || channel.ChannelType}</span>
                                <span className={`pr-dot ${channel.IsPublished ? 'live' : 'off'}`}>
                                    {channel.IsPublished ? 'Live' : channel.MappingStatus}
                                </span>
                            </div>
                            <div className="pr-channel-stats">
                                <span>{formatNumber(channel.ViewCount)} views</span>
                                <span>{formatNumber(channel.Likes)} likes</span>
                                <span>{formatNumber(channel.Comments)} comments</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="pr-analysis">
                <div className="pr-analysis-head">
                    <div>
                        <h4>What the numbers say</h4>
                        <p className="pr-muted">
                            AI reads this post’s metrics and suggests what to change next.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pr-btn accent"
                        onClick={runAnalysis}
                        disabled={analyzing || !report.HasMetrics}
                    >
                        {analyzing ? 'Analyzing…' : analysis ? 'Re-analyze' : 'Analyze metrics'}
                    </button>
                </div>

                {!report.HasMetrics && (
                    <p className="pr-muted">
                        Waiting on the first metrics sync — there is nothing to analyze yet.
                    </p>
                )}

                {analysisError && <p className="pr-error">{analysisError}</p>}

                {analysis && (
                    <div className="pr-analysis-body">
                        {analysis.Headline && <p className="pr-headline">{analysis.Headline}</p>}
                        {analysis.Assessment && <p className="pr-assessment">{analysis.Assessment}</p>}

                        {sortedRecommendations.length > 0 && (
                            <ol className="pr-recs">
                                {sortedRecommendations.map((rec, index) => (
                                    <li key={`${rec.Title}-${index}`} className="pr-rec">
                                        <div className="pr-rec-head">
                                            <span className="pr-rec-title">{rec.Title}</span>
                                            <span className={`pr-impact impact-${rec.Impact}`}>{rec.Impact}</span>
                                        </div>
                                        {rec.Why && <p className="pr-rec-why">{rec.Why}</p>}
                                        {rec.Action && <p className="pr-rec-action">{rec.Action}</p>}

                                        {/* Only suggestions the A/B system can
                                            actually run get a button; the rest
                                            are advice for the next video. */}
                                        {rec.Field && rec.ProposedValue && (
                                            <div className="pr-rec-proposal">
                                                <span className="pr-rec-proposal-label">
                                                    Proposed {rec.Field}
                                                </span>
                                                <p className="pr-rec-proposal-value">{rec.ProposedValue}</p>
                                                <button
                                                    type="button"
                                                    className="pr-btn"
                                                    onClick={() => startAbTest(seedFromRecommendation(rec, analysis))}
                                                >
                                                    Accept &amp; test
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}

                        {(analysis.AbTest || analysis.NextExperiment) && (
                            <div className="pr-experiment">
                                <span className="pr-experiment-label">Try next</span>
                                <p>{analysis.AbTest?.Hypothesis || analysis.NextExperiment}</p>

                                {analysis.AbTest && (
                                    <dl className="pr-experiment-copy">
                                        {analysis.AbTest.Title && (
                                            <>
                                                <dt>Title</dt>
                                                <dd>{analysis.AbTest.Title}</dd>
                                            </>
                                        )}
                                        {analysis.AbTest.Description && (
                                            <>
                                                <dt>Description</dt>
                                                <dd>{analysis.AbTest.Description}</dd>
                                            </>
                                        )}
                                        {analysis.AbTest.Tags?.length > 0 && (
                                            <>
                                                <dt>Hashtags</dt>
                                                <dd>{analysis.AbTest.Tags.map(t => `#${t}`).join(' ')}</dd>
                                            </>
                                        )}
                                        {analysis.AbTest.PrimaryMetric && (
                                            <>
                                                <dt>Decide on</dt>
                                                <dd>{analysis.AbTest.PrimaryMetric}</dd>
                                            </>
                                        )}
                                    </dl>
                                )}

                                <button
                                    type="button"
                                    className="pr-btn accent"
                                    onClick={() => startAbTest(analysis.AbTest ? seedFromAbTest(analysis.AbTest) : null)}
                                >
                                    Accept &amp; schedule this test
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="pr-analysis">
                <div className="pr-analysis-head">
                    <div>
                        <h4>A/B tests</h4>
                        <p className="pr-muted">
                            Run an alternate title, description, or hashtags over a date range
                            and compare it against this post.
                        </p>
                    </div>
                    {!showAbForm && (
                        <button
                            type="button"
                            className="pr-btn"
                            onClick={() => startAbTest(null)}
                            disabled={variations.length >= 10}
                            title={variations.length >= 10 ? 'A post can have at most 10 variations.' : undefined}
                        >
                            New A/B test
                        </button>
                    )}
                </div>

                {abError && <p className="pr-error">{abError}</p>}

                {showAbForm && (
                    <AbTestForm
                        // Remounts when a different suggestion is accepted, so
                        // the fields reset to the new seed.
                        key={abSeed?.note || 'blank'}
                        basePost={report}
                        seed={abSeed}
                        existingVariations={variations}
                        saving={abSaving}
                        onCancel={() => { setShowAbForm(false); setAbSeed(null); setAbError(''); }}
                        onSubmit={createVariation}
                    />
                )}

                {variations.length > 0 && (
                    <ul className="pr-variations">
                        {variations.map(variation => {
                            const result = variationResults[variation.VariationId];
                            const runState = result?.RunState || 'Scheduled';
                            const verdict = result?.Verdict || 'NoData';
                            const finished = runState === 'Complete';

                            return (
                                <li className="pr-variation" key={variation.VariationId}>
                                    <div className="pr-variation-head">
                                        <div className="pr-variation-main">
                                            <span className="pr-variation-title">
                                                {variation.AlternativeTitle || 'Untitled variation'}
                                            </span>
                                            <span className="pr-muted">
                                                {String(variation.StartDate || '').slice(0, 10)} → {String(variation.EndDate || '').slice(0, 10)}
                                                {' · '}{runState}
                                            </span>
                                            {variation.Errors && <span className="pr-error">{variation.Errors}</span>}
                                        </div>
                                        {variation.Promoted ? (
                                            <span className="pr-badge tone-live">Kept</span>
                                        ) : (
                                            <span className={`pr-verdict verdict-${verdict.toLowerCase()}`}>
                                                {VERDICT_LABELS[verdict] || verdict}
                                            </span>
                                        )}
                                    </div>

                                    {result?.Summary && <p className="pr-muted">{result.Summary}</p>}

                                    {result?.Metrics?.some(m => m.Baseline !== null || m.Variant !== null) && (
                                        <table className="pr-variation-table">
                                            <thead>
                                                <tr>
                                                    <th>Metric</th>
                                                    <th>Before</th>
                                                    <th>During</th>
                                                    <th>Change</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.Metrics.map(metric => (
                                                    <tr key={metric.Label}>
                                                        <td>{metric.Label}</td>
                                                        <td>{formatMetric(metric.Label, metric.Baseline)}</td>
                                                        <td>{formatMetric(metric.Label, metric.Variant)}</td>
                                                        <td className={changeClass(metric.PercentChange)}>
                                                            {formatChange(metric.PercentChange)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    <div className="pr-variation-actions">
                                        {finished && !variation.Promoted && (
                                            <button
                                                type="button"
                                                className="pr-btn accent"
                                                onClick={() => promoteVariation(variation.VariationId)}
                                            >
                                                Keep this version
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="pr-btn"
                                            onClick={() => removeVariation(variation.VariationId)}
                                        >
                                            {finished && !variation.Promoted ? 'Discard' : 'Remove'}
                                        </button>
                                    </div>

                                    {!variation.Promoted && (
                                        <p className="pr-muted">
                                            {finished
                                                ? 'Keeping this makes its copy the post’s own and republishes it. Discarding changes nothing — the post keeps its original copy.'
                                                : 'Discarding stops this test and puts the post back to its original copy.'}
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}

                {!showAbForm && variations.length === 0 && (
                    <p className="pr-muted">No A/B tests on this post yet.</p>
                )}
            </div>
        </div>
    );
}

// Seeded from an accepted AI suggestion when there is one, otherwise from the
// live post — an A/B variant is a copy to edit, not a blank slate.
function AbTestForm({ basePost, seed, existingVariations = [], saving, onCancel, onSubmit }) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const inTwoWeeks = new Date();
    inTwoWeeks.setDate(inTwoWeeks.getDate() + 15);

    const [title, setTitle] = useState(seed?.title || basePost?.Title || '');
    const [description, setDescription] = useState(seed?.description || '');
    const [tagText, setTagText] = useState(seed?.tags || '');
    const [startDate, setStartDate] = useState(isoDate(tomorrow));
    const [endDate, setEndDate] = useState(isoDate(inTwoWeeks));
    const [thumbnailFile, setThumbnailFile] = useState(null);

    const thumbnailPreview = useMemo(
        () => (thumbnailFile ? URL.createObjectURL(thumbnailFile) : ''),
        [thumbnailFile]
    );
    useEffect(() => () => {
        if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    }, [thumbnailPreview]);

    const issues = [];
    if (!title.trim()) issues.push('Give the variation a title.');
    if (!startDate || !endDate) issues.push('Pick a start and end date.');
    else if (startDate >= endDate) issues.push('The end date must be after the start date.');
    else if (overlapsExisting(startDate, endDate, existingVariations)) {
        // Caught here so the clash is visible while picking dates, rather than
        // arriving as a server error after filling the whole form in.
        issues.push('These dates overlap another test on this post. Only one can run at a time.');
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (issues.length) return;
        onSubmit({
            title,
            description,
            tags: tagText.split(/[\s,]+/).map(t => t.trim().replace(/^#+/, '')).filter(Boolean),
            startDate,
            endDate,
            thumbnailFile,
        });
    };

    return (
        <form className="pr-ab-form" onSubmit={handleSubmit}>
            {seed?.note && (
                <p className="pr-ab-seeded">
                    Prefilled from the AI suggestion: <strong>{seed.note}</strong>. Edit anything
                    below, then pick when it runs.
                </p>
            )}

            <label>
                <span>Alternate title</span>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Try a different hook"
                />
            </label>

            <label>
                <span>Alternate description <em>optional</em></span>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Leave blank to keep the original description"
                />
            </label>

            <label>
                <span>Alternate hashtags <em>optional</em></span>
                <input
                    type="text"
                    value={tagText}
                    onChange={(e) => setTagText(e.target.value)}
                    placeholder="e.g. shorts editing viral"
                />
            </label>

            <label>
                <span>Alternate thumbnail <em>optional</em></span>
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                />
            </label>

            {thumbnailPreview && (
                <div className="pr-ab-thumb">
                    <img src={thumbnailPreview} alt="Alternate thumbnail" />
                    <button type="button" className="pr-btn" onClick={() => setThumbnailFile(null)}>
                        Remove image
                    </button>
                </div>
            )}

            <div className="pr-ab-dates">
                <label>
                    <span>Runs from</span>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </label>
                <label>
                    <span>Until</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </label>
            </div>

            <p className="pr-muted">Date ranges cannot overlap another test on this post.</p>

            {issues.length > 0 && (
                <ul className="pr-blockers">
                    {issues.map(issue => <li key={issue}>{issue}</li>)}
                </ul>
            )}

            <div className="pr-ab-actions">
                <button type="submit" className="pr-btn accent" disabled={saving || issues.length > 0}>
                    {saving ? 'Creating…' : 'Create A/B test'}
                </button>
                <button type="button" className="pr-btn" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </form>
    );
}

export { ProjectReport };
